import React, { useState } from 'react';
import { Button, Col, Row, ProgressBar, Spinner, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { extendSampling, closeSampling } from '@lib/api';
import { ArrowRight, ConfirmSolid, IcoUnion, RejectSolid, ExclamationBrown } from '@icons';
import { formatToDecimals } from '@lib/helpers/formatting';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import SuccessModal from '@components/atomic/organisms/SucessModal/SucessModal';
import { RejectModal } from '@components/atomic/organisms';
import { Link, useLocation } from 'react-router-dom';

const StatisticsHeader = ({
	isActiveSampling,
	handleShowModal,
	setShowModalSampling,
	statistics,
	projectId,
	sampleId,
	project,
	setExternalSupplies,
	samplingInfoPreSampling,
	invoicesLength,
}) => {
	const [showModalRuleSampling, setShowModalRuleSampling] = useState(false);
	const queryClient = useQueryClient();
	const completeListParam = 'complete-list=true';
	const { pathname, search } = useLocation();
	const isCompleteList = search.includes(completeListParam);
	// console.log('samplingInfoPreSampling', samplingInfoPreSampling);

	const extendSamplingMutation = useMutation({
		mutationFn: async () => {
			const response = await extendSampling(projectId, sampleId);

			if (response.success) {
				return response;
			}
			throw new Error(response.message || 'Error extending sampling');
		},
		onSuccess: (result) => {
			setExternalSupplies(result.data);
			queryClient.invalidateQueries({ queryKey: ['samplingHistory', project.id] });
			queryClient.invalidateQueries({ queryKey: ['infoSampling', project.id] });
			if (sampleId) {
				queryClient.invalidateQueries({ queryKey: ['samplingStatistics', project.id, sampleId] });
			}
		},
		onError: (error) => {
			console.error('Error extending sampling:', error);
		},
	});

	const closeSamplingMutation = useMutation({
		mutationFn: async () => {
			const response = await closeSampling(projectId, sampleId);

			if (response.success) {
				return response;
			}
			throw new Error(response.message || 'Error closing sampling');
		},
		onSuccess: (result) => {
			setExternalSupplies(result.data);
			queryClient.invalidateQueries({ queryKey: ['samplingHistory', project.id] });
			queryClient.invalidateQueries({ queryKey: ['infoSampling', project.id] });
			if (sampleId) {
				queryClient.invalidateQueries({ queryKey: ['samplingStatistics', project.id, sampleId] });
			}
		},
		onError: (error) => {
			console.error('Error closing sampling:', error);
		},
	});

	const ConditionalWrapper = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children);

	return (
		<>
			<div className="dark-frame mb-0" style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
				{isCompleteList ? (
					<Button variant="link" as={Link} to={pathname.replace(completeListParam, '')}>
						Torna al campionamento
					</Button>
				) : null}

				{!isActiveSampling ? (
					<Row>
						<Col>
							<span className="body-text-L-bold d-block mb-1">Vuoi eseguire un campionamento?</span>
							<span className="d-block">
								Estrai dal tuo universo dei documenti un campione significativo da sottoporre a controllo specifico.
							</span>
						</Col>
						<Col className="col-auto align-self-center">
							<ConditionalWrapper
								condition={isActiveSampling && samplingInfoPreSampling.base === 0 && samplingInfoPreSampling.population === 0}
								wrapper={(children) => (
									<OverlayTrigger
										placement="top"
										overlay={
											<Tooltip>
												{invoicesLength < 1
													? 'Nessuna fattura disponibile per il campionamento.'
													: 'Tutte le fatture sono già state campionate.'}
											</Tooltip>
										}
									>
										<span className="text-danger">{children}</span>
									</OverlayTrigger>
								)}
							>
								<Button
									className="btn new custom-primary-button d-flex align-items-center"
									onClick={(e) => handleShowModal(setShowModalSampling)}
									disabled={
										invoicesLength < 1 || (isActiveSampling && samplingInfoPreSampling.base === 0 && samplingInfoPreSampling.population === 0)
									}
								>
									Esegui campionamento
									<ArrowRight
										className={`ms-1 ${invoicesLength < 1 || (isActiveSampling && samplingInfoPreSampling.base === 0 && samplingInfoPreSampling.population === 0) ? 'ico-path-fill-disabled-grey' : 'ico-path-fill-white'}`}
										style={{ width: '14px', height: '16px' }}
									/>
								</Button>
							</ConditionalWrapper>
						</Col>
					</Row>
				) : null}

				{isActiveSampling && !isCompleteList ? (
					<>
						<Row>
							<Col className="col-6">
								<Row>
									<Col className="col-auto align-self-center">
										<IcoUnion />
									</Col>
									<Col>
										<Row className="align-items-center">
											<Col className="col-auto">
												<span className="body-text-L-bold mb-1">Lista campionata</span>
											</Col>
											<Col className="col-auto pb-1 px-2">
												<Link as={Button} to={`${pathname}?${completeListParam}`} variant="link" className="btn ms-auto no-underline">
													<span style={{ color: '#1C2743', textTransform: 'none', fontWeight: '500', verticalAlign: 'middle' }}>
														vedi la lista completa
													</span>
													<ArrowRight className="ms-2 ico-path-fill-primary" />
												</Link>
											</Col>
										</Row>

										<Row className="mt-3">
											<Col className="f-400">Sottoinsieme basato sui criteri di campionamento configurati.</Col>
										</Row>
									</Col>
								</Row>
							</Col>

							<Col className="col-6 align-self-center">
								<Row className="justify-content-end align-items-center">
									<Col className="col-auto">
										<Button
											variant="link"
											className="btn ms-auto"
											style={{ textDecorationColor: '#1C2743' }}
											onClick={() => setShowModalRuleSampling(true)}
										>
											<span style={{ color: '#1C2743', textTransform: 'none', fontWeight: '500' }}>consulta le regole del bando</span>
										</Button>
									</Col>
									<Col className="col-auto">
										<Button className="btn new custom-outline-button d-flex align-items-center" onClick={closeSamplingMutation.mutate}>
											Concludi la fase di controllo
											{closeSamplingMutation.isPending && (
												<Spinner
													as="span"
													className="ms-2 ico-path-fill-white"
													animation="border"
													size="sm"
													role="status"
													aria-hidden="true"
													style={{ verticalAlign: 'revert-layer' }}
												/>
											)}
										</Button>
									</Col>
								</Row>
							</Col>
						</Row>

						<hr style={{ borderColor: '#2A3A64', opacity: 1 }} />

						<Row className="mt-4">
							<Col className="col-7" style={{ borderRight: '1px solid #2A3A64' }}>
								<Row className="justify-content-start">
									<Col className="col-9">
										<span className="f-700">
											{statistics.checkCompleted + statistics.checkCompletedWithErrors ?? 0}/{statistics.totalInvoices ?? 0} catene
											documentali controllate
										</span>
									</Col>
									<Col className="col-2 text-end">
										<span className="f-700">
											{formatToDecimals(
												((statistics.checkCompletedWithErrors + statistics.checkCompleted) / statistics.totalInvoices) * 100,
												0,
											)}
											%
										</span>
									</Col>
								</Row>

								<Row className="mt-2">
									<Col className="col-11">
										<ProgressBar>
											<ProgressBar
												variant="danger"
												className="new"
												now={statistics.percentageCompletedWithErrors ?? 0}
												key={'red' + statistics.percentageCompletedWithErrors ?? 0} //per forzare il rerender quando cambiano i valori
												label={`${formatToDecimals(statistics.percentageCompletedWithErrors, 0)}%`}
												visuallyHidden
												striped
											/>
											<ProgressBar
												variant="success"
												className="new"
												now={statistics.percentageCompleted ?? 0}
												key={'green' + statistics.percentageCompleted ?? 0} //per forzare il rerender quando cambiano i valori
												label={`${formatToDecimals(statistics.percentageCompleted, 0)}%`}
												visuallyHidden
											/>
										</ProgressBar>
									</Col>
								</Row>

								<Row className="mt-3 align-items-center">
									<Col className="col-auto d-flex align-items-center pt-1 pe-1">
										<i className="icon-square bg-green"></i>
									</Col>
									<Col className="col-auto">
										<ConfirmSolid width={20} height={20} />
									</Col>
									<Col className="ps-0 pt-1">
										<span className="f-700">
											{statistics.checkCompleted ?? 0} catene documentali conformi ({formatToDecimals(statistics.percentageCompleted, 0)}%)
										</span>
										<span className="f-400 ps-2">{formatToDecimals(statistics.completedAmount)}€ eleggibili su importo campionato</span>
									</Col>
								</Row>

								<Row className="mt-3 align-items-center">
									<Col className="col-auto d-flex align-items-center pt-1 pe-1">
										<i className="icon-square bg-red-stripes"></i>
									</Col>
									<Col className="col-auto">
										<RejectSolid width={20} height={20} />
									</Col>
									<Col className="ps-0 pt-1">
										<span className="f-700">
											{statistics.checkCompletedWithErrors ?? 0} catene documentali non conformi (
											{formatToDecimals(statistics.percentageCompletedWithErrors, 0)}%)
										</span>
										<span className="f-400 ps-2">{formatToDecimals(statistics.errorAmount)}€ non eleggibili su importo complessivo</span>
									</Col>
								</Row>
							</Col>

							<Col className="col-5 align-content-end px-3">
								<Row className="justify-content-between">
									<Col>
										<span className="f-700">
											{statistics.checkAssignedCompleted ?? 0}/{statistics.totalAssignedInvoices ?? 0} catene documentali controllate
											assegnate a me
										</span>
									</Col>
									<Col className="col-auto">
										<span className="f-700">{formatToDecimals(statistics.percentageAssignedCompleted, 0)}%</span>
									</Col>
								</Row>

								<Row className="mt-2">
									<Col>
										<ProgressBar
											className="new blue"
											now={statistics.percentageAssignedCompleted ?? 0}
											label={`${formatToDecimals(statistics.percentageAssignedCompleted, 0)}%`}
											visuallyHidden
										/>
									</Col>
								</Row>
							</Col>
						</Row>

						{statistics.percentageAmountError >= 2 ? (
							<Row className="mt-5 mb-3 new-alert-banner mx-0">
								<Col>
									<Row>
										<Col className="col-auto pe-2">
											<ExclamationBrown fill="#995C00" width={20} height={20} />
										</Col>
										<Col className="ps-0">
											<span className="f-700 text-uppercase d-block" style={{ color: '#995C00' }}>
												ATTENZIONE:
											</span>
										</Col>
									</Row>
									<Row className="mt-2">
										<span className="f-700 d-block">
											Hai superato la percentuale di catene documentali non conformi:{' '}
											{formatToDecimals(statistics.percentageAmountError, 0)}%
										</span>
									</Row>
									<Row>
										<span className="f-400">
											(percentuale massima accettabile: {formatToDecimals(statistics.maxErrorsAmountPercentage, 0)}%)
										</span>
									</Row>
								</Col>

								<Col className="col-auto align-self-center px-0">
									<Button className="btn new custom-primary-button d-flex align-items-center" onClick={extendSamplingMutation.mutate}>
										Aggiungi catene documentali al campionamento
										{extendSamplingMutation.isPending && (
											<Spinner
												as="span"
												className="ms-2 ico-path-fill-white"
												animation="border"
												size="sm"
												role="status"
												aria-hidden="true"
												style={{ verticalAlign: 'revert-layer' }}
											/>
										)}
									</Button>
								</Col>
							</Row>
						) : null}
					</>
				) : null}
			</div>

			<Modal size="lg" show={showModalRuleSampling} onHide={() => setShowModalRuleSampling(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title>Regole di campionamento del bando</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<span className="new-subtitle f-24">{project?.samplingRuleAssignment?.samplingRule?.name}</span>
					<span className="d-block mt-2">{project?.samplingRuleAssignment?.samplingRule?.description}</span>
				</Modal.Body>
				<Modal.Footer />
			</Modal>

			<SuccessModal
				show={extendSamplingMutation.isSuccess}
				onHide={extendSamplingMutation.reset}
				message="Estensione del campione eseguita con successo!"
			/>

			<RejectModal
				show={extendSamplingMutation.isError}
				onHide={extendSamplingMutation.reset}
				message={extendSamplingMutation?.error?.message || "Si è verificato un errore durante l'operazione."}
				tryAgain={extendSamplingMutation.mutate}
			/>

			<SuccessModal
				show={closeSamplingMutation.isSuccess}
				onHide={closeSamplingMutation.reset}
				message="Chiusura del campionamento eseguita con successo!"
			/>

			<RejectModal
				show={closeSamplingMutation.isError}
				onHide={closeSamplingMutation.reset}
				message={closeSamplingMutation?.error?.message || "Si è verificato un errore durante l'operazione."}
				tryAgain={closeSamplingMutation.mutate}
			/>
		</>
	);
};
export default StatisticsHeader;
